import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { FC, useRef, useEffect, useCallback } from "react";
import classNames from "classnames";
import query from "../../query";
import Refresh from "../../components/Refresh";
import Ticket from "./Ticket";
import { Scroll, Wrapper } from "./styled";
import Loading from "../../components/Icons/Loading";

const MyBets: FC = () => {
  const getBetTickets = ({ page }: { page: number }) => 
    query.getBetTickets({
      page,
      limit: 10,
      type: ["normal", "step", "comboStep"],
      criteriaDate: "ALL",
    }).queryFn();

    const getBetTicketsTotalDocs = query.getBetTickets({
      page: 1,
      limit: 10,
      type: ["normal", "step", "comboStep"],
      criteriaDate: "ALL",
    });
    useQuery(
      getBetTicketsTotalDocs.key,
      getBetTicketsTotalDocs.queryFn
    );

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ["betTickets"],
    ({ pageParam = 1 }) => getBetTickets({ page: pageParam }),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.page < lastPage.totalPages) {
          return lastPage.page + 1;
        }
        return undefined;
      },
    }
  );

  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: "200px",
      threshold: 0.1,
    });
    if (element) observer.observe(element);
    return () => {
      if (element) observer.unobserve(element);
    };
  }, [handleObserver]);

  const tickets = data?.pages.flatMap(page => page.docs) ?? [];

  const onClickRefresh = () => {
    refetch();
  };

  return (
    <div className="flex-1 h-full flex flex-col">
      <Wrapper>
        <Refresh
          isLoading={isLoading}
          onClick={onClickRefresh}
          initialCounter={90}
        />
      </Wrapper>

      <Scroll
        className={classNames({
          "h-full flex justify-center items-center": isLoading,
        })}
      >
        {isLoading && <Loading size={25} />}
        {!isLoading && tickets.length === 0 && (
          <p className="p-5">
            You do not place bets, Placed bets will appear here.
          </p>
        )}
        {tickets.map((ticket: any) => (
          <Ticket
            key={ticket.betId}
            betId={ticket.betId}
            betAt={ticket.createDate}
            status={ticket.status}
            matchs={ticket.sport}
            betType={ticket.betType}
            amount={Math.abs(ticket.betAmt.$numberDecimal)}
            currency={ticket.currency}
          />
        ))}
        <div ref={loadMoreRef} style={{ height: 1, width: "100%" }} />
        {isFetchingNextPage && <Loading size={20} />}
      </Scroll>
    </div>
  );
};

export default MyBets;
